/**
 * Counts down to the daily shipping cut-off shown in the buy box.
 *
 * The deadline arrives as an absolute epoch timestamp in `data-deadline`,
 * computed in Liquid from the shop's own clock. That keeps the element correct
 * regardless of the visitor's time zone and regardless of how long the page sat
 * in a cache: the instant itself does not move, only our distance from it.
 *
 * When the deadline passes the element swaps to the fallback message instead of
 * showing 00:00:00, so a late-evening visitor is told the truth rather than
 * being sold an order deadline that has already gone.
 */

const pad = (n) => String(Math.floor(n)).padStart(2, '0');

class ShippingCountdown extends HTMLElement {
  connectedCallback() {
    const deadline = Number(this.dataset.deadline);

    if (!Number.isFinite(deadline) || deadline <= 0) {
      this.#expire();
      return;
    }

    this.deadline = deadline * 1000;
    this.hoursEl = this.querySelector('[data-countdown-hours]');
    this.minutesEl = this.querySelector('[data-countdown-minutes]');
    this.secondsEl = this.querySelector('[data-countdown-seconds]');

    this.#tick();

    // A one-second interval is enough: the seconds field is the fastest thing
    // on screen, and drift of well under a second is invisible here.
    this.timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  #tick() {
    const remaining = this.deadline - Date.now();

    if (remaining <= 0) {
      clearInterval(this.timer);
      this.#expire();
      return;
    }

    const totalSeconds = remaining / 1000;

    if (this.hoursEl) this.hoursEl.textContent = pad(totalSeconds / 3600);
    if (this.minutesEl) this.minutesEl.textContent = pad((totalSeconds % 3600) / 60);
    if (this.secondsEl) this.secondsEl.textContent = pad(totalSeconds % 60);
  }

  #expire() {
    this.dataset.state = 'expired';
  }
}

if (!customElements.get('shipping-countdown')) {
  customElements.define('shipping-countdown', ShippingCountdown);
}
